import axios from 'axios'

const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('user_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Search
export interface SearchCreate {
  query: string
  query_built?: boolean
  lite_mode?: boolean
  max_results?: number
}

export interface SearchResponse {
  search_id: string
  query: string
  query_built: boolean
  lite_mode: boolean
  max_results: number
  total_count?: number
  status: string
  error_message?: string
  progress_fetched?: number
  progress_local?: number
  progress_total?: number
  created_at: string
  completed_at?: string
}

export interface ArticleResponse {
  pmid: string
  title?: string
  authors?: string[]
  authors_full?: string
  affiliations?: string[]
  author_emails?: string[]
  journal?: string
  journal_abbrev?: string
  year?: number
  abstract?: string
  keywords?: string[]
  mesh_terms?: string[]
  pub_types?: string[]
  doi?: string
  issn?: string
  quartile?: string
  sjr_score?: number
  h_index?: number
  citations?: number
  citations_per_year?: number
  rcr?: number
  field_citation_rate?: number
  nih_percentile?: number
}

export interface SearchResult {
  search: SearchResponse
  pmids: string[]
  total: number
  enriched_count: number
  // Legacy: only populated when older clients hit a pre-pagination backend.
  articles?: ArticleResponse[]
  count?: number
}

export interface SearchRowsRequest {
  offset: number
  limit: number
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
  pmids?: string[]
}

export interface SearchRowsResponse {
  rows: any[]
  total: number
}

// Filters
export interface FilterCondition {
  param: string
  condition: string
  value: any
  use_regex?: boolean
  case_sensitive?: boolean
}

export interface FilterRequest {
  filters: FilterCondition[]
}

export interface FilterResponse {
  pmids: string[]
  count: number
}

// Sessions
export interface SessionCreate {
  name: string
  search_id?: string
}

export interface SessionResponse {
  session_id: string
  user_id?: string
  name: string
  search_id?: string
  filters: any[]
  datasets: any[]
  analyses: any[]
  rag_index_id?: string
  chat_history: any[]
  created_at: string
  last_accessed: string
  expires_at: string
}

export interface SessionUpdate {
  name?: string
  filters?: any[]
  datasets?: any[]
  analyses?: any[]
  chat_history?: any[]
}

// Datasets
export interface DatasetCreate {
  name: string
  search_id: string
  session_id?: string
  filter_state?: any
  pmids?: string[]
  analysis_results?: any
  analysis_type?: string
  ai_insights?: string
  topic_names?: Record<string, string>
}

export interface DatasetResponse {
  dataset_id: string
  session_id: string
  name: string
  search_id: string
  filter_state?: any
  analysis_results?: any
  analysis_type?: string
  ai_insights?: string
  topic_names?: Record<string, string>
  annotation_analysis_id?: string | null
  annotation_analysis_name?: string | null
  article_count?: number
  has_rag?: boolean
  created_at: string
}

export const searchApi = {
  createSearch: (data: SearchCreate) =>
    api.post('/api/search', data).then(r => r.data as SearchResponse),

  getSearch: (searchId: string) =>
    api.get(`/api/search/${searchId}`).then(r => r.data as SearchResult),

  getSearchRows: (searchId: string, body: SearchRowsRequest) =>
    api.post(`/api/search/${searchId}/rows`, body).then(r => r.data as SearchRowsResponse),

  deleteSearch: (searchId: string) =>
    api.delete(`/api/search/${searchId}`).then(r => r.data),

  getArticleDetails: (searchId: string, pmid: string) =>
    api.get(`/api/search/${searchId}/articles/${pmid}`).then(r => r.data as ArticleDetailsResponse),
}

export const filterApi = {
  applyFilters: (searchId: string, filters: FilterCondition[]) =>
    api.post(`/api/search/${searchId}/filter`, { filters }).then(r => r.data as FilterResponse),
  
  getFilterOptions: (searchId: string, param: string) =>
    api.get(`/api/search/${searchId}/filter-options/${param}`).then(r => r.data),
}

export const sessionApi = {
  createSession: (data: SessionCreate) =>
    api.post('/api/sessions', data).then(r => r.data as SessionResponse),
  
  getSession: (sessionId: string) =>
    api.get(`/api/sessions/${sessionId}`).then(r => r.data as SessionResponse),
  
  listMySessions: () =>
    api.get('/api/sessions/my').then(r => r.data as SessionResponse[]),
  
  updateSession: (sessionId: string, data: SessionUpdate) =>
    api.put(`/api/sessions/${sessionId}`, data).then(r => r.data as SessionResponse),
  
  deleteSession: (sessionId: string) =>
    api.delete(`/api/sessions/${sessionId}`).then(r => r.data),
}

export const datasetApi = {
  createDataset: (data: DatasetCreate) =>
    api.post('/api/datasets', data).then(r => r.data as DatasetResponse),
  
  updateDataset: (datasetId: string, data: DatasetCreate) =>
    api.put(`/api/datasets/${datasetId}`, data).then(r => r.data as DatasetResponse),
  
  listDatasets: (sessionId?: string) =>
    api.get('/api/datasets/my', { params: { session_id: sessionId } }).then(r => r.data as DatasetResponse[]),
  
  listMyDatasets: () =>
    api.get('/api/datasets/my').then(r => r.data as DatasetResponse[]),
  
  getDataset: (datasetId: string) =>
    api.get(`/api/datasets/${datasetId}`).then(r => r.data),
  
  deleteDataset: (datasetId: string) =>
    api.delete(`/api/datasets/${datasetId}`).then(r => r.data),

  mergeDatasets: (data: { name: string; dataset_ids: string[]; session_id?: string | null }) =>
    api.post('/api/datasets/merge', data).then(r => r.data as DatasetResponse),

  replaceDatasetArticles: (
    datasetId: string,
    data: { search_id: string; pmids?: string[] | null },
  ) =>
    api.post(`/api/datasets/${datasetId}/replace-articles`, data).then(r => r.data as DatasetResponse),

  getArticleDetails: (datasetId: string, pmid: string) =>
    api.get(`/api/datasets/${datasetId}/articles/${pmid}/details`).then(r => r.data as ArticleDetailsResponse),

  getArticleDetailsBulk: (datasetId: string, pmids: string[]) =>
    api.post(`/api/datasets/${datasetId}/articles/details-bulk`, { pmids }).then(r => r.data as { details: ArticleDetailsResponse[] }),

  annotateWithAnalysis: (datasetId: string, analysisId: string) =>
    api.post(`/api/datasets/${datasetId}/annotate`, { analysis_id: analysisId }).then(r => r.data as {
      message: string
      annotation_analysis_id: string
      analysis_type: string
      analysis_name: string
    }),

  clearAnnotation: (datasetId: string) =>
    api.delete(`/api/datasets/${datasetId}/annotate`).then(r => r.data as { message: string }),
}

export interface ArticleDetailsResponse {
  pmid: string
  abstract?: string | null
  authors_full?: string | null
  affiliations?: string[] | null
  author_emails?: string[] | null
}

// Analytics
export interface YearDistributionItem {
  year: number
  count: number
}

export interface QuartileCount {
  quartile: string
  count: number
}

export interface JournalCount {
  journal: string
  count: number
}

export interface KeywordCount {
  keyword: string
  count: number
}

export interface AuthorDistribution {
  authors: number
  count: number
}

export interface CitationStats {
  mean: number
  median: number
  max: number
  min: number
  total_with_data: number
}

export interface AnalyticsOverview {
  total: number
  year_distribution: YearDistributionItem[]
  quartile_counts: QuartileCount[]
  top_journals: JournalCount[]
  author_distribution: AuthorDistribution[]
  top_keywords: KeywordCount[]
  citation_stats: CitationStats
}

export interface TrendItem {
  year: number
  count: number
}

export interface TrendsData {
  trends: TrendItem[]
  cumulative: TrendItem[]
}

export interface KeywordTrend {
  year: number
  keywords: KeywordCount[]
}

export interface MeshTrend {
  year: number
  mesh_terms: { mesh: string; count: number }[]
}

export interface KeywordsData {
  keyword_trends: KeywordTrend[]
  mesh_trends: MeshTrend[]
  top_mesh: { mesh: string; count: number }[]
}

export interface SjrDistribution {
  range: string
  count: number
}

export interface QuartileAvgSjr {
  quartile: string
  avg_sjr: number
  count: number
}

export interface JournalMetrics {
  sjr_distribution: SjrDistribution[]
  h_index_distribution: { range: string; count: number }[]
  quartile_avg_sjr: QuartileAvgSjr[]
}

export interface QuartileTrendsData {
  quartile_trends: { year: number; Q1: number; Q2: number; Q3: number; Q4: number }[]
}

export const analyticsApi = {
  getOverview: (searchId: string) =>
    api.get(`/api/search/${searchId}/analytics/overview`).then(r => r.data as AnalyticsOverview),
  
  getTrends: (searchId: string) =>
    api.get(`/api/search/${searchId}/analytics/trends`).then(r => r.data as TrendsData),
  
  getKeywords: (searchId: string) =>
    api.get(`/api/search/${searchId}/analytics/keywords`).then(r => r.data as KeywordsData),
  
  getJournalMetrics: (searchId: string) =>
    api.get(`/api/search/${searchId}/analytics/journal-metrics`).then(r => r.data as JournalMetrics),
  
  getQuartileTrends: (searchId: string) =>
    api.get(`/api/search/${searchId}/analytics/quartile-trends`).then(r => r.data as QuartileTrendsData),
  
  fetchCitations: (searchId: string) =>
    api.post(`/api/search/${searchId}/analytics/citations`).then(r => r.data as { message: string; total: number; updated: number; note?: string }),
}

// Network
export interface NetworkNode {
  id: string
  label: string
  title: string
  year?: number
  journal?: string
  quartile?: string
  citations: number
  rcr: number
  size: number
}

export interface NetworkEdge {
  source: string
  target: string
  type: string
  weight?: number
  overlap?: number
}

export interface NetworkResponse {
  nodes: NetworkNode[]
  edges: NetworkEdge[]
  count: number
  edge_count: number
  has_citation_data: boolean
  message: string
  landmark_count?: number
}

export interface LandmarkPaper {
  pmid: string
  count: number
  title: string
  authors: string
  year?: number
  journal: string
  citing_papers: string[]
}

export interface LandmarksResponse {
  landmarks: LandmarkPaper[]
  count: number
  threshold: number
  message: string
}

export const networkApi = {
  getCitationNetwork: (searchId: string, maxNodes = 200, minCitations = 0, includeLandmarks = false, landmarkThreshold = 3) =>
    api.get(`/api/search/${searchId}/network`, { params: { max_nodes: maxNodes, min_citations: minCitations, include_landmarks: includeLandmarks, landmark_threshold: landmarkThreshold } }).then(r => r.data as NetworkResponse),
  
  getCocitationNetwork: (searchId: string, maxNodes = 100, minOverlap = 1, minCitations = 0) =>
    api.get(`/api/search/${searchId}/cocitation`, { params: { max_nodes: maxNodes, min_overlap: minOverlap, min_citations: minCitations } }).then(r => r.data as NetworkResponse),
  
  getLandmarks: (searchId: string, threshold = 3, maxLandmarks = 50) =>
    api.get(`/api/search/${searchId}/landmarks`, { params: { threshold, max_landmarks: maxLandmarks } }).then(r => r.data as LandmarksResponse),
}

export default api
